import { Typography, Box, TextField, Autocomplete } from "@mui/material";
import { useState, useMemo } from "react";
import { useDebouncedSearch } from "Hooks/useDebouncedSearch";
import { useMerchantUserSearchQuery } from "Api/Queries/Admin/useMerchantUserSearchQuery";
import type { MerchantUserDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

// One login User can hold several companies, the grant tells them apart
type MerchantUserKey = Pick<MerchantUserDto, "userID" | "identityUserID">;

const isSameMerchantUser = (a: MerchantUserKey, b: MerchantUserKey) =>
  a.userID === b.userID &&
  (a.identityUserID ?? null) === (b.identityUserID ?? null);

type Props = {
  name: string;
  label: string;
  disabled?: boolean;
  onChange: (value: MerchantUserDto | null) => void;
  value: MerchantUserKey | null;
  errorMessage?: string;
  initialMerchant?: MerchantUserDto | null;
};

export const MerchantUserAutocomplete = ({
  name,
  label,
  disabled,
  onChange,
  value,
  errorMessage,
  initialMerchant,
}: Props) => {
  const [inputValue, setInputValue] = useState("");
  const { searchTerm, setSearchTerm, debouncedSearchTerm } =
    useDebouncedSearch(300);
  const { t } = useResource();

  const { data: searchResults, isLoading } = useMerchantUserSearchQuery(
    debouncedSearchTerm,
    debouncedSearchTerm.length >= 2,
  );

  // Merge initial merchant with search results
  const options = useMemo(() => {
    const results = searchResults || [];

    // If we have an initial merchant and it's not in search results, add it
    if (
      initialMerchant &&
      !results.some((merchant) => isSameMerchantUser(merchant, initialMerchant))
    ) {
      return [initialMerchant, ...results];
    }

    return results;
  }, [searchResults, initialMerchant]);

  const selectedMerchant =
    (value &&
      options.find((merchant) => isSameMerchantUser(merchant, value))) ||
    null;

  const handleInputChange = (
    _event: unknown,
    newInputValue: string,
    reason: string,
  ) => {
    setInputValue(newInputValue);
    // Only trigger search when user is typing, not when selecting/clearing options
    if (reason === "input") {
      setSearchTerm(newInputValue);
    }
  };

  return (
    <div>
      {label && (
        <Typography
          component="label"
          htmlFor={name}
          sx={{ mb: 1, display: "block" }}
        >
          {label}
        </Typography>
      )}
      <Autocomplete
        options={options}
        getOptionLabel={({ companyName, companyNumber }) =>
          companyName ? `${companyName} | ${companyNumber}` : ""
        }
        value={selectedMerchant}
        onChange={(_, newValue) => {
          onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        loading={isLoading}
        disabled={disabled}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              value
                ? ""
                : t(
                    Resources.Components.Shared.MerchantUserAutocomplete
                      .Placeholder,
                  )
            }
            fullWidth
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: errorMessage ? "error.main" : "divider",
                borderRadius: 1,
              },
            }}
            error={!!errorMessage}
            helperText={errorMessage || " "}
          />
        )}
        renderOption={(props, merchant) => (
          <Box
            component="li"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={0.5}
            {...props}
            key={`${merchant.userID}-${merchant.identityUserID ?? ""}`}
            sx={{ textAlign: "left" }}
          >
            <Typography fontWeight={500}>
              {merchant.companyName ?? ""}
            </Typography>
            {merchant.companyNumber && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={300}
              >
                {merchant.companyNumber}
              </Typography>
            )}
          </Box>
        )}
        noOptionsText={
          searchTerm.length < 2
            ? t(Resources.Components.Shared.MerchantUserAutocomplete.MinLength)
            : t(Resources.Components.Shared.MerchantUserAutocomplete.NoResults)
        }
        slotProps={{
          listbox: {
            sx: {
              "& .MuiAutocomplete-option": {
                textAlign: "left !important",
                justifyContent: "flex-start !important",
                alignItems: "flex-start !important",
              },
            },
          },
        }}
        sx={{
          "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
            textAlign: "left !important",
            justifyContent: "flex-start !important",
          },
        }}
        isOptionEqualToValue={isSameMerchantUser}
        fullWidth
      />
    </div>
  );
};
